/**
 * Shared fade-in, from hung-hola's assets/js/fade-in.js. Two ways in:
 *
 * 1. initFadeIn() (assets/js/index.entry.ts, every page, GSAP + ScrollTrigger from Nectar): the attributes, as in
 *    hung-hola's blocks, so a block needs no code of its own:
 *    - data-px-fade-in on a container: its direct children fade in and rise one after another, in DOM order, once,
 *      when the container's top reaches 80% of the screen;
 *    - data-px-fade-in-item on any descendants: only those animate, in DOM order, however deep they sit;
 *    - data-px-fade-in="load": content in the first screen, plays on page load (hidden before first paint by
 *      html.px-fade-in-pending, set in <head> by inc/config/enqueue-assets.php, with a 3 s fallback);
 *    - data-px-fade-in-after="<name>" (with "load"): waits for the document event "px:<name>", or the attribute
 *      data-px-<name> on <html> if it already fired; plays anyway after 8 s (hero: "hero-intro");
 *    - data-px-fade-in-replay: plays again each time it comes back down the screen (event px:fade-in-reset);
 *    - data-px-fade-in-item="desktop": takes part only above 1280 on a landscape screen (at ≤1280 or portrait it
 *      shows from first paint, e.g. the hero's text: the LCP on phones);
 *    - data-px-fade-in-stagger="<s>" on a container: its own time between two items (default MOTION.reveal.stagger);
 *    - data-px-fade-in-item="center" on a run of consecutive items (slider cards): spreads out from the item in the
 *      middle of the screen to both sides, one ring every MOTION.reveal.spread s;
 *    - when done the container gets data-px-faded-in and fires px:faded-in (portfolio waits for it).
 * 2. fadeIn(container, items) (Get in touch, without GSAP: the Web Animations API).
 *
 * Items outside the screen sideways show at once and take no turn. With ?still (screenshot compare), reduced motion
 * or no GSAP everything shows at once. Timing: MOTION.reveal (assets/js/motion.ts), measured on growtrics.ai.
 */
import { MOTION, isStill, motionLib } from './motion';

export { isStill };

// fadeIn()'s timing: MOTION.reveal in the Web Animations API's units
export const REVEAL = {
	duration: MOTION.reveal.duration * 1000, // ms, each item
	stagger: MOTION.reveal.stagger * 1000, // ms between two items
	rise: MOTION.reveal.rise, // px each item rises
	easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)', // GSAP power3.out
};

export function fadeIn(container: HTMLElement, items: HTMLElement[]): void {
	if (!items.length || isStill() || !('IntersectionObserver' in window)) {
		return;
	}
	items.forEach((item) => (item.style.opacity = '0'));

	const play = (): void => {
		const shown = items.filter((item) => {
			const box = item.getBoundingClientRect();
			return box.right > 0 && box.left < window.innerWidth;
		});
		items.forEach((item) => item.style.removeProperty('opacity'));
		shown.forEach((item, index) => {
			item.animate(
				[
					{ opacity: 0, transform: `translateY(${REVEAL.rise}px)` },
					{ opacity: 1, transform: 'none' },
				],
				{ duration: REVEAL.duration, delay: index * REVEAL.stagger, easing: REVEAL.easing, fill: 'backwards' }
			);
		});
	};

	// The container's top at 80% of the screen (or already above it)
	const observer = new IntersectionObserver(
		(entries) => {
			if (entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top < 0)) {
				observer.disconnect();
				play();
			}
		},
		{ rootMargin: '0px 0px -20% 0px' }
	);
	observer.observe(container);
}

const START = 'top 80%'; // the container's top reaches 80% of the screen
const AFTER_FALLBACK = 8; // s, a data-px-fade-in-after container plays even if its event never comes
const PROPS = 'opacity,visibility,transform,translate,rotate,scale'; // cleared at the end; other inline styles stay

// Start time of each item: one stagger after the other in DOM order, except a run of data-px-fade-in-item="center"
// items, which starts from the item nearest the middle of the screen and spreads out one item width per spread
function delays(items: HTMLElement[], stagger: number): (index: number) => number {
	const { spread } = MOTION.reveal;
	const out: number[] = [];
	let time = 0;
	for (let i = 0; i < items.length; ) {
		if (items[i].dataset.pxFadeInItem !== 'center') {
			out.push(time);
			time += stagger;
			i++;
			continue;
		}
		let end = i;
		while (end < items.length && items[end].dataset.pxFadeInItem === 'center') {
			end++;
		}
		const middle = window.innerWidth / 2;
		const width = Math.max(1, items[i].getBoundingClientRect().width);
		const offsets = items.slice(i, end).map((item) => {
			const box = item.getBoundingClientRect();
			return Math.abs(box.left + box.width / 2 - middle);
		});
		const nearest = Math.min(...offsets);
		let last = 0;
		offsets.forEach((offset) => {
			const step = Math.round((offset - nearest) / width);
			out.push(time + step * spread);
			last = Math.max(last, step);
		});
		time += last * spread + stagger;
		i = end;
	}
	return (index) => out[index];
}

export function initFadeIn(): void {
	const root = document.documentElement;
	const reveal = (): void => root.classList.remove('px-fade-in-pending');
	const lib = motionLib();
	if (!lib) {
		reveal();
		return;
	}
	const { gsap, ScrollTrigger } = lib;
	const { duration, rise, ease } = MOTION.reveal;
	// from() tweens to the items' own values, so the CSS pre-hide goes first; the set() below hides them again
	// inline in the same task, before any paint
	reveal();

	document.querySelectorAll<HTMLElement>('[data-px-fade-in]').forEach((container) => {
		const marked = container.querySelectorAll<HTMLElement>('[data-px-fade-in-item]');
		const touch = window.matchMedia('(width <= 1280px), (orientation: portrait)').matches;
		const items = (marked.length ? [...marked] : ([...container.children] as HTMLElement[])).filter(
			(item) => !(touch && item.dataset.pxFadeInItem === 'desktop')
		);
		const stagger = Number(container.dataset.pxFadeInStagger) || MOTION.reveal.stagger;
		if (!items.length) {
			return;
		}
		const onLoad = container.dataset.pxFadeIn === 'load';
		const after = onLoad && container.dataset.pxFadeInAfter;
		gsap.set(items, { autoAlpha: 0, y: rise });
		let played = false;
		const play = (): void => {
			if (played) {
				return;
			}
			// A container already past START fires at once, while the block scripts after this one have not run
			// yet: wait for them, or a slider is measured unplaced
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', play, { once: true });
				return;
			}
			played = true;
			gsap.set(items, { clearProps: PROPS });
			const shown = items.filter((item) => {
				const box = item.getBoundingClientRect();
				return box.right > 0 && box.left < window.innerWidth;
			});
			const done = (): void => {
				container.setAttribute('data-px-faded-in', '');
				container.dispatchEvent(new CustomEvent('px:faded-in'));
			};
			gsap.from(shown, { autoAlpha: 0, y: rise, duration, stagger: delays(shown, stagger), ease, clearProps: PROPS, onComplete: done });
		};
		if (!onLoad && container.hasAttribute('data-px-fade-in-replay')) {
			ScrollTrigger.create({
				trigger: container,
				start: 'top bottom',
				onLeaveBack: () => {
					gsap.killTweensOf(items);
					gsap.set(items, { autoAlpha: 0, y: rise });
					played = false;
					container.removeAttribute('data-px-faded-in');
					container.dispatchEvent(new CustomEvent('px:fade-in-reset'));
				},
			});
			ScrollTrigger.create({ trigger: container, start: START, onEnter: play });
			return;
		}
		if (!onLoad) {
			ScrollTrigger.create({ trigger: container, start: START, once: true, onEnter: play });
			return;
		}
		if (!after || root.hasAttribute(`data-px-${after}`)) {
			play();
			return;
		}
		document.addEventListener(`px:${after}`, play, { once: true });
		window.setTimeout(play, AFTER_FALLBACK * 1000);
	});
}
